/**
 * Períodos, como o usuário pensa neles — Frente F, Story F.2.
 *
 * ## Por que existe
 *
 * O dashboard (2.3) já tinha `diaIso`, `mesAtual` e `mesAnterior`, e a lista de
 * orçamentos passou a precisar de "hoje", "7 dias" e "30 dias". A página de
 * logs (L.4) vai precisar dos mesmos. Três telas calculando data cada uma do
 * seu jeito é onde nasce a divergência de **um dia** — que ninguém reporta como
 * defeito, só como "esse filtro não bate".
 *
 * Todas recebem `agora` por parâmetro: são funções puras, testáveis sem mexer
 * no relógio, e o chamador é quem decide quando ler a hora. Essa disciplina já
 * evitou um defeito real — no dashboard, os atalhos liam o relógio uma vez, no
 * carregamento da página, e "Mês atual" passava a devolver o mês errado numa
 * aba deixada aberta.
 *
 * ## O fuso é o do NAVEGADOR, de propósito
 *
 * O servidor corta o dia em `America/Sao_Paulo`; aqui vale o relógio de quem
 * olha a tela. Para um usuário no Brasil os dois concordam, que é o cenário do
 * produto — e "hoje" tem de ser o hoje de quem está clicando.
 */

export interface Periodo {
  /** `AAAA-MM-DD`, inclusivo. */
  de: string;
  /** `AAAA-MM-DD`, **inclusivo** — o dia inteiro entra. */
  ate: string;
}

/** `AAAA-MM-DD` a partir dos componentes **locais** da data. */
export function diaIso(d: Date): string {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${String(d.getFullYear())}-${mes}-${dia}`;
}

/** Só o dia de hoje — as duas pontas iguais. */
export function hoje(agora: Date): Periodo {
  const d = diaIso(agora);
  return { de: d, ate: d };
}

/**
 * Os últimos `n` dias, **contando hoje**.
 *
 * "7 dias" é hoje e os seis anteriores, não hoje menos sete. É como as pessoas
 * contam ("essa semana"), e é o que evita a lista mostrar oito dias sob um
 * rótulo que diz sete.
 */
export function ultimosDias(agora: Date, n: number): Periodo {
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - (n - 1));
  return { de: diaIso(inicio), ate: diaIso(agora) };
}

/** Do dia 1 do mês corrente até hoje. */
export function mesAtual(agora: Date): Periodo {
  return { de: diaIso(new Date(agora.getFullYear(), agora.getMonth(), 1)), ate: diaIso(agora) };
}

/**
 * O mês anterior INTEIRO — do dia 1 ao último dia.
 *
 * `new Date(ano, mes, 0)` devolve o último dia do mês anterior, o que resolve
 * fevereiro e ano bissexto sem tabela: dia 0 de março é 28 ou 29 conforme o
 * ano. Contar "30 dias atrás" daria um recorte que não é mês nenhum.
 */
export function mesAnterior(agora: Date): Periodo {
  const ultimoDoAnterior = new Date(agora.getFullYear(), agora.getMonth(), 0);
  const primeiroDoAnterior = new Date(agora.getFullYear(), agora.getMonth() - 1, 1);
  return { de: diaIso(primeiroDoAnterior), ate: diaIso(ultimoDoAnterior) };
}

/** Dois períodos são o mesmo quando as duas pontas batem. */
export function mesmoPeriodo(a: Periodo, b: Periodo): boolean {
  return a.de === b.de && a.ate === b.ate;
}

/**
 * `true` quando o período **não** pode ir para a API.
 *
 * Campo vazio é o estado normal de quem está trocando a data — o navegador
 * limpa o valor antes de aceitar o novo. Mandar `?de=` nesse instante renderia
 * um 400 do servidor no meio de uma digitação que ainda nem terminou.
 */
export function periodoIncompleto(p: Periodo): boolean {
  return p.de === '' || p.ate === '';
}

/**
 * `true` quando o início é depois do fim.
 *
 * A guarda de vazio vem antes porque `'' > '2026-08-26'` é **falso** — o
 * intervalo não pareceria invertido, e a busca sairia com a data pela metade.
 */
export function periodoInvertido(p: Periodo): boolean {
  return !periodoIncompleto(p) && p.de > p.ate;
}
